/**
 * WeaveIn.ai Platform - Workflows API Service
 * TypeScript service layer for workflow and automation management
 */

import axios, { AxiosInstance } from 'axios';

import { config as productConfig } from '@/src/config';
// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type WorkflowTriggerType =
  | 'manual'
  | 'enrollment_created'
  | 'enrollment_status_changed'
  | 'activity_completed'
  | 'time_based';

/**
 * `notification` is the legacy "System Notification" step: no engine handler
 * acts on it, and the palette no longer offers it. TRUS-686 replaced it with
 * `notify_channel` (Post to channel) and `notify_staff` (Notify staff).
 */
export type StepType = 'task' | 'notification' | 'notify_channel' | 'notify_staff' | 'communication' | 'wait_for_response' | 'approval' | 'condition' | 'api_call' | 'wait' | 'form' | 'billing' | 'pricing_agent' | 'prediction_gate' | 'recommendation_action' | 'external_data_ingest' | 'navigate_contact';

export type WorkflowInstanceStatus = 'running' | 'completed' | 'failed' | 'cancelled' | 'paused';

export type AutomationTriggerType =
  | 'enrollment_status_changed'
  | 'activity_created'
  | 'document_uploaded'
  | 'goal_completed'
  | 'scheduled';

export interface WorkflowTemplateBase {
  name: string;
  description?: string;
  trigger_type: WorkflowTriggerType;
  trigger_config?: Record<string, any>;
  is_active?: boolean;
  program_id?: number;
}

export interface WorkflowTemplateCreate extends WorkflowTemplateBase {
  name: string;
  trigger_type: WorkflowTriggerType;
}

export interface WorkflowTemplate extends WorkflowTemplateBase {
  id: number;
  cbo_id: number;
  name: string;
  trigger_type: WorkflowTriggerType;
  is_active: boolean;
  created_ts: string;
  last_updated_ts: string;
  program_name?: string;
}

export interface WorkflowTemplateListResponse {
  workflows: WorkflowTemplate[];
  total: number;
  page: number;
  page_size: number;
}

export interface WorkflowStepBase {
  step_order: number;
  step_type: StepType;
  step_name: string;
  step_config: Record<string, any>;
  required?: boolean;

  /** Fields the API has always returned and this interface never declared, so
   *  every consumer that wanted them reached through `as any` or did without.
   *  The canvas needs them to say anything useful inside an expanded box. */
  step_code?: string | null;
  description?: string | null;
  instructions?: string | null;
  default_assignee_role?: string | null;
  estimated_duration_hours?: number | null;
  due_offset_days?: number | null;
  depends_on_step_id?: number | null;
  is_critical?: boolean;
  form_template_id?: number | null;

  /**
   * Where this step is performed — a key from `page_capabilities`, never a URL.
   * A route changes and the link rots silently; a page_key does not. It is also
   * what lets the agent answer "how do I ..." from this organization's own
   * process rather than from how the product generally works.
   *
   * It is a reference, never a grant: `can_open_page` still decides who may go
   * there.
   */
  page_key?: string | null;
  /** Which part of that page. "The Conversations tab" is the difference between
   *  a step somebody can follow and one that sends them hunting. */
  location_hint?: string | null;
  /** FALSE for steps nobody performs on a screen — an ingest, an automated
   *  approval. Without it, "not a screen" and "not mapped yet" are both an
   *  empty page_key and the gap counter lists the first kind forever. */
  is_user_facing?: boolean;
  /** An optional section heading. Onboarding adoption writes
   *  "<phase> — <milestone>"; any workflow past a dozen steps wants sections. */
  step_group?: string | null;

  /** Where the box sits on the canvas. NULL is not missing data: it means the
   *  canvas lays this step out automatically, which is right for every step
   *  until somebody drags it. Always read and written as a pair. */
  position_x?: number | null;
  position_y?: number | null;
  /** Overrides the shape implied by step_type. NULL means derive it, so the
   *  default lives in one place and a new step type needs no migration. */
  canvas_shape?: string | null;
}

export interface CanvasNodePosition {
  step_id: number;
  position_x: number;
  position_y: number;
  canvas_shape?: string | null;
  /** The order this step now runs in, when the canvas derives order from
   *  layout. Omitted leaves the order alone, so a client that only arranges
   *  boxes cannot renumber a process by forgetting a field. */
  step_order?: number;
}

export interface CanvasLayoutResult {
  saved: number;
  /** Step ids the server refused because they are not in this workflow. Not
   *  empty means the canvas and the server disagree about what the workflow
   *  contains, and the boxes will jump back on reload. */
  rejected: number[];
}

/** One row of the page registry, for the picker. */
export interface WorkflowPageOption {
  page_key: string;
  page_name: string;
  section: string | null;
}

/** How many user-facing steps still do not say where they happen.
 *  Both halves, because 23 of 180 and 23 of 24 are different situations. */
export interface WorkflowStepGaps {
  unmapped: number;
  mapped: number;
  total: number;
}

/** An onboarding template this organization can build a Setup workflow from.
 *
 *  The counts are split by class on purpose. An onboarding template is a setup
 *  checklist PLUS a project plan, and only the setup half describes the product
 *  — so the screen has to say what will and will not cross over before anybody
 *  presses the button. A single "37 tasks" number is the one that invites the
 *  question these fields exist to answer. */
export interface OnboardingTemplateSummary {
  id: number;
  name: string;
  description: string | null;
  is_platform_template: boolean;
  setup_workflow_template_id: number | null;
  setup_workflow_name: string | null;
  has_setup_workflow: boolean;
  total_tasks: number;
  /** Setting up a product capability. Crosses over. */
  configuration_tasks: number;
  /** Repeatable work. Crosses over. On today's data this is zero everywhere:
   *  an onboarding plan does not contain process definitions. */
  process_tasks: number;
  /** The onboarding project itself — kickoff calls, agreements, go-live.
   *  Stays in the plan and is never a workflow step. */
  engagement_tasks: number;
  /** Configuration tasks nobody has tied to a capability yet. A gap somebody
   *  can close in a minute, and invisible unless it is shown. */
  configuration_without_capability: number;
}

export interface SetupWorkflowBuildResult {
  workflow_template_id: number;
  /** False when the Setup workflow already existed, so the caller can say
   *  "updated" rather than claim to have created it twice. */
  created: boolean;
  added: number;
  already_present: number;
  /** Engagement tasks deliberately left in the onboarding plan. Returned rather
   *  than inferred so the UI can say where they went. */
  excluded: number;
}

export type WorkflowNoteType = 'meeting' | 'decision' | 'requirement' | 'reference';

export type WorkflowAttachmentKind = 'link' | 'file' | 'jira';

/** Something a note refers to.
 *
 *  One ordered list rather than three, because a reader wants what the note
 *  points at — not a section per type. A `file` REFERENCES the media library;
 *  the bytes are never copied onto the note. */
export interface WorkflowNoteAttachment {
  kind: WorkflowAttachmentKind;
  url?: string;
  label?: string;
  /** jira only. Kept apart from the url because the KEY is the identity — the
   *  url changes if the Jira site moves and the key does not. */
  key?: string;
  asset_id?: number;
  mime?: string;
  size?: number;
}

/** Why a workflow is the way it is.
 *
 *  A template records WHAT the steps are; nothing recorded why. `requirement` is
 *  kept apart from `decision` because a decision can be revisited and a payer
 *  rule cannot. */
export interface WorkflowNote {
  id: number;
  note_type: WorkflowNoteType;
  title: string | null;
  body: string;
  workflow_step_id: number | null;
  step_name: string | null;
  meeting_date: string | null;
  attendees: string[];
  decided_on: string | null;
  decided_by: number | null;
  decided_by_name: string | null;
  supersedes_note_id: number | null;
  attachments: WorkflowNoteAttachment[];
  created_by: number | null;
  created_by_name: string | null;
  created_ts: string;
  updated_ts: string;
  /** Derived from whether a later note replaces this one, never stored. A
   *  decision wrongly shown as current is the failure this field prevents. */
  is_superseded: boolean;
}

export interface WorkflowNoteInput {
  note_type: WorkflowNoteType;
  body: string;
  title?: string | null;
  workflow_step_id?: number | null;
  meeting_date?: string | null;
  attendees?: string[];
  decided_on?: string | null;
  supersedes_note_id?: number | null;
  /** Omitting leaves them alone on update; sending `[]` removes them all.
   *  Collapsing the two would make "remove the last attachment" impossible. */
  attachments?: WorkflowNoteAttachment[];
}

export interface WorkflowStepCreate extends WorkflowStepBase {
  workflow_template_id: number;
}

export interface WorkflowStep extends WorkflowStepBase {
  id: number;
  workflow_template_id: number;
  cbo_id: number;
  created_ts: string;
}

export interface WorkflowInstanceBase {
  enrollment_id: number;
  current_step_order?: number;
  status?: WorkflowInstanceStatus;
  started_at?: string;
  completed_at?: string;
  error_message?: string;
}

export interface WorkflowInstanceCreate extends WorkflowInstanceBase {
  workflow_template_id: number;
  enrollment_id?: number;
  member_id?: number;
}

export interface WorkflowInstance extends WorkflowInstanceBase {
  id: number;
  workflow_template_id: number;
  enrollment_id?: number;
  cbo_id: number;
  status: WorkflowInstanceStatus;
  created_ts: string;
  last_updated_ts: string;
  workflow_name?: string;
  member_name?: string;
  program_name?: string;
}

export interface WorkflowInstanceListResponse {
  instances: WorkflowInstance[];
  total: number;
  page: number;
  page_size: number;
}

export interface WorkflowStepExecutionBase {
  status: string;
  started_at?: string;
  completed_at?: string;
  result_data?: Record<string, any>;
  error_message?: string;
}

export interface WorkflowStepExecutionCreate extends WorkflowStepExecutionBase {
  workflow_instance_id: number;
  step_id: number;
}

export interface WorkflowStepExecution extends WorkflowStepExecutionBase {
  id: number;
  workflow_instance_id: number;
  step_id: number;
  cbo_id: number;
  created_ts: string;
  step_name?: string;
  step_type?: StepType;
}

// ============================================================================
// WORKFLOW INSTANCE DETAIL TYPES (for step-by-step execution)
// ============================================================================

export type StepInstanceStatus = 'pending' | 'in_progress' | 'completed' | 'skipped' | 'failed';

export interface StepInstanceDetail {
  id: number;
  workflow_instance_id: number;
  workflow_step_id: number;
  status: StepInstanceStatus;
  assigned_to?: number;
  due_date?: string;
  started_at?: string;
  completed_at?: string;
  completion_notes?: string;
  created_ts: string;
  step_name: string;
  step_code?: string;
  step_type: StepType;
  step_order: number;
  estimated_duration_hours?: number;
  due_offset_days?: number;
  depends_on_step_id?: number;
  default_assignee_role?: string;
  action_required?: string;
  is_critical: boolean;
  is_automated: boolean;
  step_config: Record<string, any>;
  form_template_id?: number;
  assigned_to_name?: string;
}

export interface WorkflowInstanceDetail {
  id: number;
  workflow_template_id: number;
  cbo_id: number;
  enrollment_id?: number;
  member_id?: number;
  status: WorkflowInstanceStatus;
  progress_percentage: number;
  current_step_id?: number;
  assigned_to?: number;
  started_at?: string;
  due_date?: string;
  completed_at?: string;
  created_ts: string;
  template_name?: string;
  template_description?: string;
  member_name?: string;
  total_steps: number;
  completed_steps: number;
  steps: StepInstanceDetail[];
}

export type StepExecuteAction = 'start' | 'complete' | 'skip' | 'fail';

export interface StepExecuteRequest {
  action: StepExecuteAction;
  notes?: string;
  assigned_to?: number;
}

export interface StepExecuteResponse {
  success: boolean;
  step_id: number;
  step_name: string;
  new_status: string;
  message: string;
  workflow_progress: number;
  next_step_id?: number;
  next_step_name?: string;
}

export interface AutomationRuleBase {
  rule_name: string;
  trigger_type: AutomationTriggerType;
  trigger_conditions: Record<string, any>;
  actions: Record<string, any>[];
  is_active?: boolean;
  priority?: number;
  program_id?: number;
}

export interface AutomationRuleCreate extends AutomationRuleBase {
  rule_name: string;
  trigger_type: AutomationTriggerType;
  trigger_conditions: Record<string, any>;
  actions: Record<string, any>[];
}

export interface AutomationRule extends AutomationRuleBase {
  id: number;
  cbo_id: number;
  rule_name: string;
  trigger_type: AutomationTriggerType;
  is_active: boolean;
  created_ts: string;
  last_updated_ts: string;
  program_name?: string;
}

export interface AutomationRuleListResponse {
  rules: AutomationRule[];
  total: number;
  page: number;
  page_size: number;
}

// ============================================================================
// API CLIENT
// ============================================================================

class WorkflowsAPI {
  private client: AxiosInstance;

  constructor(baseURL: string = (productConfig.weaveinApi.baseUrl ?? '').trim()) {
    this.client = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add auth token interceptor
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      if (cboId) {
        config.headers['X-CBO-ID'] = cboId;
      }

      return config;
    });
  }

  // ========================================================================
  // WORKFLOW TEMPLATE ENDPOINTS
  // ========================================================================

  async createWorkflowTemplate(data: WorkflowTemplateCreate): Promise<WorkflowTemplate> {
    const response = await this.client.post<WorkflowTemplate>('/api/workflows/templates', data);
    return response.data;
  }

  async listWorkflowTemplates(params?: {
    page?: number;
    page_size?: number;
    program_id?: number;
    is_active?: boolean;
    target_entity_type?: string;
  }): Promise<WorkflowTemplateListResponse> {
    const response = await this.client.get<WorkflowTemplateListResponse>('/api/workflows/templates', { params });
    return response.data;
  }

  async getWorkflowTemplate(templateId: number): Promise<WorkflowTemplate> {
    const response = await this.client.get<WorkflowTemplate>(`/api/workflows/templates/${templateId}`);
    return response.data;
  }

  async updateWorkflowTemplate(templateId: number, data: Partial<WorkflowTemplateBase>): Promise<WorkflowTemplate> {
    const response = await this.client.put<WorkflowTemplate>(`/api/workflows/templates/${templateId}`, data);
    return response.data;
  }

  async deleteWorkflowTemplate(templateId: number): Promise<void> {
    await this.client.delete(`/api/workflows/templates/${templateId}`);
  }

  // ========================================================================
  // WORKFLOW STEP ENDPOINTS
  // ========================================================================

  async createWorkflowStep(data: WorkflowStepCreate): Promise<WorkflowStep> {
    const response = await this.client.post<WorkflowStep>(`/api/workflows/templates/${data.workflow_template_id}/steps`, data);
    return response.data;
  }

  async listWorkflowSteps(templateId: number): Promise<WorkflowStep[]> {
    const response = await this.client.get<WorkflowStep[]>(`/api/workflows/templates/${templateId}/steps`);
    return response.data;
  }

  async updateWorkflowStep(stepId: number, data: Partial<WorkflowStepBase>): Promise<WorkflowStep> {
    const response = await this.client.put<WorkflowStep>(`/api/workflows/steps/${stepId}`, data);
    return response.data;
  }

  async deleteWorkflowStep(stepId: number): Promise<void> {
    await this.client.delete(`/api/workflows/steps/${stepId}`);
  }

  // ========================================================================
  // WORKFLOW INSTANCE ENDPOINTS
  // ========================================================================

  async createWorkflowInstance(data: WorkflowInstanceCreate): Promise<WorkflowInstance> {
    const response = await this.client.post<WorkflowInstance>('/api/workflows/instances', data);
    return response.data;
  }

  async listWorkflowInstances(params?: {
    page?: number;
    page_size?: number;
    workflow_template_id?: number;
    enrollment_id?: number;
    status?: WorkflowInstanceStatus;
  }): Promise<WorkflowInstanceListResponse> {
    const response = await this.client.get<WorkflowInstanceListResponse>('/api/workflows/instances', { params });
    return response.data;
  }

  async getWorkflowInstance(instanceId: number): Promise<WorkflowInstance> {
    const response = await this.client.get<WorkflowInstance>(`/api/workflows/instances/${instanceId}`);
    return response.data;
  }

  async updateWorkflowInstance(instanceId: number, data: Partial<WorkflowInstanceBase>): Promise<WorkflowInstance> {
    const response = await this.client.put<WorkflowInstance>(`/api/workflows/instances/${instanceId}`, data);
    return response.data;
  }

  async deleteWorkflowInstance(instanceId: number): Promise<void> {
    await this.client.delete(`/api/workflows/instances/${instanceId}`);
  }

  // ========================================================================
  // WORKFLOW STEP EXECUTION ENDPOINTS
  // ========================================================================

  async listStepExecutions(instanceId: number): Promise<WorkflowStepExecution[]> {
    const response = await this.client.get<WorkflowStepExecution[]>(
      `/api/workflows/instances/${instanceId}/executions`
    );
    return response.data;
  }

  // ========================================================================
  // WORKFLOW INSTANCE DETAIL & STEP EXECUTION ENDPOINTS
  // ========================================================================

  async getWorkflowInstanceDetails(instanceId: number): Promise<WorkflowInstanceDetail> {
    const response = await this.client.get<WorkflowInstanceDetail>(
      `/api/workflows/instances/${instanceId}/details`
    );
    return response.data;
  }

  async executeWorkflowStep(
    instanceId: number,
    stepId: number,
    request: StepExecuteRequest
  ): Promise<StepExecuteResponse> {
    const response = await this.client.post<StepExecuteResponse>(
      `/api/workflows/instances/${instanceId}/steps/${stepId}/execute`,
      request
    );
    return response.data;
  }

  async getEnrollmentWorkflowInstances(enrollmentId: number): Promise<WorkflowInstance[]> {
    const response = await this.client.get<WorkflowInstanceListResponse>(
      '/api/workflows/instances',
      { params: { enrollment_id: enrollmentId } }
    );
    return response.data.instances;
  }

  // ========================================================================
  // AUTOMATION RULE ENDPOINTS
  // ========================================================================

  async createAutomationRule(data: AutomationRuleCreate): Promise<AutomationRule> {
    const response = await this.client.post<AutomationRule>('/api/workflows/automation-rules', data);
    return response.data;
  }

  async listAutomationRules(params?: {
    page?: number;
    page_size?: number;
    program_id?: number;
    is_active?: boolean;
  }): Promise<AutomationRuleListResponse> {
    const response = await this.client.get<AutomationRuleListResponse>('/api/workflows/automation-rules', { params });
    return response.data;
  }

  async getAutomationRule(ruleId: number): Promise<AutomationRule> {
    const response = await this.client.get<AutomationRule>(`/api/workflows/automation-rules/${ruleId}`);
    return response.data;
  }

  async updateAutomationRule(ruleId: number, data: Partial<AutomationRuleBase>): Promise<AutomationRule> {
    const response = await this.client.put<AutomationRule>(`/api/workflows/automation-rules/${ruleId}`, data);
    return response.data;
  }

  async deleteAutomationRule(ruleId: number): Promise<void> {
    await this.client.delete(`/api/workflows/automation-rules/${ruleId}`);
  }

  // ========================================================================
  // PROCESS DESIGN — where a step happens, and what is still unmapped
  // ========================================================================

  /**
   * Pages a step can point at.
   *
   * Read from the registry rather than typed free-hand, because a mistyped
   * page_key is invisible: it fails `can_open_page` for everyone and the step
   * silently never appears.
   */
  async listPageOptions(): Promise<WorkflowPageOption[]> {
    const response = await this.client.get<{ pages: WorkflowPageOption[] }>('/api/workflows/page-options');
    return response.data.pages;
  }

  /** Save where every box that moved now sits.
   *
   *  One request, not one per box: dragging a corner of the diagram moves a
   *  dozen steps, and a dozen PUTs half-apply on a dropped connection. */
  async saveCanvasLayout(templateId: number, nodes: CanvasNodePosition[]): Promise<CanvasLayoutResult> {
    const response = await this.client.put<CanvasLayoutResult>(
      `/api/workflows/templates/${templateId}/canvas`,
      { nodes }
    );
    return response.data;
  }

  async getStepGaps(): Promise<WorkflowStepGaps> {
    const response = await this.client.get<WorkflowStepGaps>('/api/workflows/step-gaps');
    return response.data;
  }

  // ========================================================================
  // ONBOARDING TEMPLATES, AUTHORED HERE
  // ========================================================================

  async listOnboardingTemplates(): Promise<OnboardingTemplateSummary[]> {
    const response = await this.client.get<{ templates: OnboardingTemplateSummary[] }>(
      '/api/workflows/onboarding-templates'
    );
    return response.data.templates;
  }

  /** Build, or top up, the Setup workflow for an onboarding template.
   *
   *  Re-runnable: a second call adds the configuration tasks that have appeared
   *  since and leaves every existing step alone, including ones this
   *  organization edited or wrote itself. */
  async buildSetupWorkflow(onboardingTemplateId: number): Promise<SetupWorkflowBuildResult> {
    const response = await this.client.post<SetupWorkflowBuildResult>(
      `/api/workflows/onboarding-templates/${onboardingTemplateId}/build-setup`
    );
    return response.data;
  }

  // ========================================================================
  // WORKFLOW NOTES — meetings, decisions, requirements, references
  // ========================================================================

  async listWorkflowNotes(templateId: number, noteType?: WorkflowNoteType): Promise<WorkflowNote[]> {
    const response = await this.client.get<{ notes: WorkflowNote[] }>(
      `/api/workflows/templates/${templateId}/notes`,
      { params: noteType ? { note_type: noteType } : undefined }
    );
    return response.data.notes;
  }

  async createWorkflowNote(templateId: number, note: WorkflowNoteInput): Promise<{ id: number }> {
    const response = await this.client.post<{ id: number }>(
      `/api/workflows/templates/${templateId}/notes`,
      note
    );
    return response.data;
  }

  async updateWorkflowNote(
    templateId: number,
    noteId: number,
    note: Partial<WorkflowNoteInput>
  ): Promise<{ id: number }> {
    const response = await this.client.put<{ id: number }>(
      `/api/workflows/templates/${templateId}/notes/${noteId}`,
      note
    );
    return response.data;
  }

  /** Retires the note. Soft on the server: a note another note supersedes must
   *  not vanish from under it, because the chain is how anybody reconstructs
   *  what changed and when. */
  /** Upload a file and get back a reference a note can hold.
   *
   *  Goes to the media library, which already stores per organization and
   *  already returns a URL — a second file store for notes would be a second
   *  thing to back up, scan and expire. */
  async uploadNoteFile(file: File): Promise<WorkflowNoteAttachment> {
    const form = new FormData();
    form.append('file', file);
    const response = await this.client.post<{
      id: number;
      name: string;
      storage_url: string;
      mime_type: string;
      file_size: number;
    }>('/api/media-library/assets/upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return {
      kind: 'file',
      url: response.data.storage_url,
      label: response.data.name,
      asset_id: response.data.id,
      mime: response.data.mime_type,
      size: response.data.file_size,
    };
  }

  async deleteWorkflowNote(templateId: number, noteId: number): Promise<void> {
    await this.client.delete(`/api/workflows/templates/${templateId}/notes/${noteId}`);
  }
}

// Export singleton instance
export const workflowsAPI = new WorkflowsAPI();

// ============================================================================
// REACT HOOKS
// ============================================================================

export { default as useWorkflows } from '@/hooks/useWorkflows';
export { default as useAutomationRules } from '@/hooks/useAutomationRules';
export { default as useWorkflowInstance } from '@/hooks/useWorkflowInstance';
export { useEnrollmentWorkflows } from '@/hooks/useWorkflowInstance';
